<template>
  <div
    class="review-rating-wrapper"
    :class="{
      white: white,
      'margin-top-xxs': !isReviewletOptimizationEnabled || (isReviewletOptimizationEnabled && !isMobile),
    }"
  >
    <Skeleton v-if="showSkeleton" height="20.5px" width="50%" />
    <template v-else>
      <div v-if="hasReviews" class="review-rating" :class="{ mobile: isReviewletOptimizationEnabled && isMobile }">
        <button
          class="clickable"
          :aria-label="accessibleReviewCount"
          @click="handleKeyboardTag"
          @keydown.enter="handleKeyboardTag"
          @keydown.space.prevent="handleKeyboardTag"
        >
          <ReviewTop
            :review-count="reviewCount || 0"
            :rating="reviewsRating"
            :is-review-optimization-enabled="isReviewletOptimizationEnabled && isMobile"
          >
            <template #stars>
              <Icon :icon="csgReviewStarFilled" size="s" :theme="starIconTheme" />
            </template>
          </ReviewTop>
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import Icon from '@atomic-ui/icon/Icon';
import { csgReviewStarFilled } from '@csgSvgIcons/icons';
import ReviewTop from '@nuxt-common/media-card-large/app/components/experiments/ReviewTop.vue';
import Skeleton from '@atomic-ui/skeleton/Skeleton';

import useReviews from '~/modules/reviews/composables/useReviews';
import useReviewsRedirect from '~/modules/reviews/composables/useReviewsRedirect';
import useReviewsStatistics from '~/modules/reviews/composables/useReviewsStatistics';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { reviewTopLinkTag } from '../analytics/reviews';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { LOADING_STATE } from '../constants/reviews';

const props = defineProps({
  white: {
    type: Boolean,
    default: false,
  },
  scrollBehavior: {
    type: String as PropType<ScrollBehavior>,
    default: 'auto',
  },
});

const { isConsolidatedThirdVariantFlag } = useProductFlags();
const { averageRating: rating, totalCount } = useReviewsStatistics();
const { isReviewletOptimizationEnabled } = useFeatureEligibility();
const { isMobile } = useDeviceType();

const hasReviews = computed(() => !!totalCount.value);
const { loadingState } = useReviews();
const showSkeleton = computed(() => isConsolidatedThirdVariantFlag && loadingState.value === LOADING_STATE.LOADING);

const starIconTheme = props.white ? 'light' : 'dark';

const isLessThenThousand = (count: number) => {
  return count < 1000;
};

const thousandReminder = (count: number) => {
  return Math.floor(count / 1000);
};

const formatReviewCount = (count: number): string => {
  if (isLessThenThousand(count)) return String(count);
  return `${thousandReminder(count)}k+`;
};

const accessibleReviewCount = computed(() => {
  const ratingMessage = `${rating.value} stars out of 5;`;
  if (isLessThenThousand(totalCount.value || 0)) {
    return ratingMessage.concat(`${totalCount.value} reviews; See all reviews`);
  }
  return ratingMessage.concat(`over ${thousandReminder(totalCount.value ?? 0)} thousand reviews; See all reviews`);
});

const reviewCount = computed(() => (isReviewletOptimizationEnabled && isMobile.value ? formatReviewCount(totalCount.value || 0) : totalCount.value));

const reviewsRating = computed(() => {
  if (!rating.value) return null;
  const r = Number(rating.value);
  return Number.isFinite(r) ? r.toFixed(1) : null;
});

const { scrollToReviews } = useReviewsRedirect();

const handleKeyboardTag = () => {
  reviewTopLinkTag();
  scrollToReviews(props.scrollBehavior);
};
</script>






<style lang="scss" scoped>
@import '~@csgIcons/new/review-star-filled-light-small';

.review-rating-wrapper {
  &.white * {
    color: $white;
  }
}

.review-rating {
  width: fit-content;

  &.mobile {
    margin-left: auto;
  }
}
.clickable {
  cursor: pointer;

  :deep(.rating-description span) {
    text-decoration: underline;
    text-decoration-thickness: rem-calc(1);
    text-underline-offset: rem-calc(2);
    cursor: pointer;
  }

  &:hover {
    :deep(.rating-description span) {
      opacity: 0.65;
    }
  }
}


.clickable {
  :deep(.rating-description span) {
    font-size: rem-calc(14);
    font-weight: 400;
  }
}
</style>